<template>
  <div class="container">
    <Toolbar class="mt-2 mb-2">
      <template #start>
        <h2>
          <span v-if="isUpdate">
            {{ $t('venuePage.updateVenue') }}
          </span>
          <span v-else>{{ $t('venuePage.createVenue') }}</span>
        </h2>
      </template>
    </Toolbar>
    <form
      v-if="!success"
      @submit.prevent="validForm"
    >
      <div class="p-input mb-3">
        <label
          for="code"
          class="required-input"
        >{{
          $t('entity.venue.code') }}</label>
        <InputText
          id="code"
          v-model.trim="v$.venue.code.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.venue.code.$error }"
          type="text"
          autocomplete="on"
        />
        <div
          v-for="error of v$.venue.code.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="french-wording"
          class="required-input"
        >{{ $t('entity.venue.frenchWording') }}</label>
        <InputText
          id="french-wording"
          v-model="v$.venue.frenchWording.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.venue.frenchWording.$error }"
          type="text"
          required
        />
        <div
          v-for="error of v$.venue.frenchWording.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="english-wording"
          class="required-input"
        >{{ $t('entity.venue.englishWording') }}</label>
        <InputText
          id="english-wording"
          v-model="v$.venue.englishWording.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.venue.englishWording.$error }"
          type="text"
          required
        />
        <div
          v-for="error of v$.venue.englishWording.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="address"
          class="required-input"
        >{{ $t('entity.venue.address') }}</label>
        <InputAddress
          id="address"
          v-model="v$.venue.address.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.venue.address.$error }"
          type="text"
          required
        />
        <div
          v-for="error of v$.venue.address.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="timezone"
          class="required-input"
        >{{ $t('entity.venue.timeZone') }}</label>
        <Dropdown
          id="timezone"
          v-model="v$.venue.timeZone.$model"
          class="col-12 mt-2 p-0"
          :options="timezones"
          :class="{'invalid-input': v$.venue.timeZone.$error }"
        />
        <div
          v-for="error of v$.venue.timeZone.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <Message
        v-if="errors"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>
      <div class="my-3 flex justify-content-between flex-wrap">
        <Button
          :label="$t('action.back')"
          icon="pi pi-arrow-left"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="toVenueTable"
        />
        <Button
          type="submit"
          :label="isUpdate ? $t('action.crud.update') : $t('action.crud.create')"
          icon="pi pi-save"
          class="p-button-rounded p-button-outlined"
          :disabled="saveButtonDisabled"
          :loading="isSaving"
        />
      </div>
    </form>
  </div>
</template>

<script>
import {useVuelidate} from "@vuelidate/core";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Message from "primevue/message";
import {maxLength, required} from "@/i18n/i18nValidators";
import Toolbar from "primevue/toolbar";
import Dropdown from "primevue/dropdown";
import InputAddress from "@/components/InputAddress.vue";
import moment from "moment-timezone";

export default {
  name: 'VenueForm',
  components: {InputAddress, InputText, Button, Message, Toolbar, Dropdown },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      venue: {
        id: null,
        code: '',
        frenchWording: '',
        englishWording: '',
        address: null,
        timeZone: 'Europe/Paris',
        active: true
      },
      timezones: [],
      isUpdate: false,
      errors: null,
      success: false,
      isSaving: false
    }
  },
  validations() {
    return {
      venue: {
        code: { required, maxLength: maxLength(255) },
        frenchWording: { required, maxLength: maxLength(255) },
        englishWording: { required, maxLength: maxLength(255) },
        address: { required },
        timeZone: { required }
      }
    }
  },
  computed: {
    saveButtonDisabled() {
      return this.v$.$invalid;
    }
  },
  async mounted() {
    this.timezones = moment.tz.names();
    if (this.$route.params.id) {
      this.fetchVenue();
      this.isUpdate = true;
    }
  },
  methods: {
    async fetchVenue() {
      try {
        this.venue = await this.$venueService.fetchVenue(this.$route.params.id);
        if (!this.venue) await this.$router.push("/venue");
      } catch (error) {
        this.$emitter.emit('toast', {
          severity: 'error',
          message: error.response.data.map(error => this.$t(error)).join(', ')
        });
        await this.$router.push("/venue");
      }
    },
    async validForm() {
      this.errors = null;
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;

      this.isSaving = true;

      if (this.venue.id) {
        await this.updateVenue()
      } else {
        await this.createVenue()
      }
    },
    async createVenue() {
      try {
        const created = await this.$venueService.createVenue(this.venue)
        if (created) {
          this.success = true;
          this.$emitter.emit('toast', { severity: 'success', message: 'venuePage.createSuccess' })
          this.errors = null;
          this.isSaving = false;
          this.toVenueTable();
        }
      } catch (err) {
        this.success = false;
        this.errors = err.response.data;
        this.isSaving = false;
      }
    },
    async updateVenue() {
      try {
        const updated = await this.$venueService.updateVenue(this.venue)
        if (updated) {
          this.success = true;
          this.$emitter.emit('toast', { severity: 'success', message: 'venuePage.updateSuccess' })
          this.errors = null;
          this.isSaving = false;
          this.toVenueTable();
        }
      } catch (err) {
        this.success = false;
        this.errors = err.response.data;
        this.isSaving = false;
      }
    },
    toVenueTable() {
      this.$router.push({ name: 'venue' })
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
